<template>
  <div>
    <label
      class="text-input block mb-2"
    >
      {{ $t('payout.calculation') }}
      <span class="font-normal">{{ `(${$t('pages.driver.payoutList.editPayoutModal.driverLanguage')}: ${$t(`language.${driver?.user?.language}`)})` }}</span>
    </label>
    <div class="flex flex-col gap-y-2">
      <div v-for="(entry, i) in internalEntries" :key="entry.id" class="flex gap-x-4">
        <FormInput
          v-if="entry.type === 'manual'"
          v-model="entry.subject"
          class="grow"
        />
        <FormInput
          v-else
          :error="internalError?.[`entries[${i}]`]?.subject"
          :model-value="`${$t('payoutEntryType.'+entry.type)} ${entry.subject}`"
          class="grow"
          disabled
        />
        <FormInput
          v-model="entry.amount"
          :error="internalError?.[`entries[${i}]`]?.amount"
          type="number"
          class="w-1/4"
        />
        <div class="w-8 mr-2 flex item-center">
          <TrashIcon class="cursor-pointer" @click="deleteEntry(entry)" />
        </div>
      </div>
    </div>
    <div class="flex justify-center pt-5 pb-5">
      <div class="w-8">
        <AddIcon class="cursor-pointer" @click="addNew()" />
      </div>
    </div>
    <div class="total-box">
      <div class="grow pl-5">
        {{ $t('payout.total') }}
      </div>
      <FormInput
        :model-value="total"
        class="w-1/4"
        disabled
      />
      <div class="w-8 mr-2" />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useVModel } from '@vueuse/core';
import FormInput from '@utils/inputs/Input.vue';
import AddIcon from '@images/panes/add.svg?component';
import TrashIcon from '@images/trash.svg?component';
import { computed } from 'vue';
import { v4 as uuidv4 } from 'uuid';
import type { PayoutEntry, Driver } from '@/entities/Entities';
import type { PartialOnly } from '@/utils/types';
import {useDeletePrompt} from '@utils/prompt/injects';

interface Props {
  entries: PartialOnly<PayoutEntry, '@id'|'@type'>[];
  errors: unknown;
  driver: Driver | undefined;
}

const emit = defineEmits(['update:entries', 'update:errors']);
const props = defineProps<Props>();
const internalEntries = useVModel(props, 'entries', emit);
const internalError = useVModel(props, 'errors', emit);

const total = computed(() => {
  const num = internalEntries.value.reduce((acc, entry) => acc + entry.amount, 0);
  if (typeof num !== 'number' || Math.abs(num) < 0.005) return (0).toFixed(2);

  return num.toFixed(2);
});

const addNew = () => {
  internalEntries.value.push({
    id: uuidv4(),
    payout: null,
    subject: '',
    comment: null,
    amount: 0,
    type: 'manual',
  });
};

const prompt = useDeletePrompt();

const deleteEntry = async (entry: PayoutEntry) => {
  if (entry.type === 'manual' || await prompt?.()) {
    internalEntries.value = internalEntries.value.filter((e) => e.id !== entry.id);
  }
};
</script>

<style lang="scss" scoped>
.total-box{
  @apply flex gap-x-4;
  @apply items-center;
  @apply pt-5;
  @apply border-t border-light-gray;
}
</style>
